import { all, call, put, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/shared/modules/errorHandler/actions';
import config from '@/config';
import { getRequestedGameType } from '@/features/gameSession/queryParams';
import { USE_DUMMY_GAME_DATA } from '@/shared/config/env';
import { processRequest, RequestMethod } from '@/shared/services/api';
import * as gameActions from '../store/actions';
import { GAME_TYPES, gameActionTypes } from '../store/constants';
import { GameData } from '../types';

import bouncySumsDummyData from '@/features/bouncySums/bouncySumsDummyData.json';
import laneChooserDummyData from '@/features/laneChooser/laneChooserDummyData.json';
import letterPartyDummyData from '@/features/letterParty/letterPartyDummyData.json';
import timeGameDummyData from '@/features/timeGame/timeGameDummyData.json';

import type { SagaIterator } from 'redux-saga';

export default function* (): Generator {
	yield all([
		takeLatest(gameActionTypes.GET_GAME_DATA_REQUEST, handleGetGameDataRequest),
		takeLatest(gameActionTypes.FINISH_GAME_REQUEST, handleFinishGameRequest),
	]);
}

const getCurrentRequestedGameType = () => {
	return getRequestedGameType(window.location.pathname, new URLSearchParams(window.location.search));
};

const getMockGameData = (gameType = getCurrentRequestedGameType()) => {
	if (gameType === GAME_TYPES.TIME_GAME) {
		return timeGameDummyData.user_learning_path_section;
	}

	if (gameType === GAME_TYPES.BOUNCY_SUMS_GAME) {
		return bouncySumsDummyData;
	}

	if (gameType === GAME_TYPES.LANE_CHOOSER_GAME) {
		return laneChooserDummyData.user_learning_path_section;
	}

	if (gameType === GAME_TYPES.LETTER_PARTY_GAME) {
		return letterPartyDummyData.user_learning_path_section;
	}

	return timeGameDummyData.user_learning_path_section;
};

const getRequestMethod = (method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') => {
	switch (method) {
		case 'POST':
			return RequestMethod.POST;
		case 'PUT':
			return RequestMethod.PUT;
		case 'PATCH':
			return RequestMethod.PATCH;
		case 'DELETE':
			return RequestMethod.DELETE;
		case 'GET':
		default:
			return RequestMethod.GET;
	}
};

export function* handleGetGameDataRequest(): SagaIterator {
	try {
		const requestedGameType = getCurrentRequestedGameType();

		if (USE_DUMMY_GAME_DATA) {
			const gameData = getMockGameData(requestedGameType) as unknown as GameData;

			yield put(gameActions.getGameDataSuccess(gameData));
			return;
		}

		const dataHref = config.data_href;

		if (!dataHref?.href) {
			throw new Error('Game data config is missing');
		}

		const response: { data: any } = yield call(
			processRequest,
			dataHref.href,
			getRequestMethod(dataHref.method),
			{},
		);
		const gameData = response.data?.user_learning_path_section ?? response.data;

		if (!gameData) {
			throw new Error('Game data response is empty');
		}

		yield put(gameActions.getGameDataSuccess(gameData as GameData));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(gameActions.getGameDataError());
	}
}

export function* handleFinishGameRequest({ payload }: any): SagaIterator {
	const endHref = config.end_href;
	const method = getRequestMethod(endHref?.method);
	const requestPayload = payload || {};

	try {
		if (USE_DUMMY_GAME_DATA) {
			yield put(gameActions.finishGameSuccess());
			return;
		}

		if (endHref?.href) {
			yield call(processRequest, endHref.href, method, requestPayload);
		}

		yield put(gameActions.finishGameSuccess());
	} catch (e) {
		yield put(gameActions.finishGameError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}
